import { WellDetailsStates } from 'business/models/CustomModels';
import { IWellImageParams } from 'business/models/Models';
import { SizeDetails } from 'components/canvas/constants';
import {
  DropImageParameters,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import {
  AnnotationUpdateDetails,
  CrystalUpdateDetails,
  DrawingType,
  DrawingUpdateDetails,
  DropLocationUpdateDetails,
  PoiUpdateDetails,
  RoiUpdateDetails,
  RulerUpdateDetails,
  ScribbleUpdateDetails,
} from 'components/well/drawingManager/constants';
import { convertPointsFromCanvasToDB } from 'components/well/drawingManager/utils';
import { getRegionIdCached, getWellImageParams } from 'components/well/utils';

export interface DrawingsFrame extends SizeDetails {
  crWidth: number;
  crHeight: number;
  scalingWidth: number;
  scalingHeight: number;
}

export const getDrawingsFrame = (tpObject: TransformPoints): DrawingsFrame => ({
  canvasWidth: tpObject.canvasWidth,
  canvasHeight: tpObject.canvasHeight,
  crWidth: tpObject.crWidth,
  crHeight: tpObject.crHeight,
  scalingWidth: tpObject.scalingWidth,
  scalingHeight: tpObject.scalingHeight,
});

export const isDrawingsFrameStale = (
  frame: DrawingsFrame,
  tpObject: TransformPoints,
): boolean =>
  frame.canvasWidth !== tpObject.canvasWidth ||
  frame.canvasHeight !== tpObject.canvasHeight ||
  frame.crWidth !== tpObject.crWidth ||
  frame.crHeight !== tpObject.crHeight ||
  frame.scalingWidth !== tpObject.scalingWidth ||
  frame.scalingHeight !== tpObject.scalingHeight;

export const getTransformedUpdateDrawingsRequestParameters = (
  drawingType: DrawingType,
  drawingUpdateDetails: DrawingUpdateDetails,
  tpObject: TransformPoints,
): {
  xOffSet: number;
  yOffSet: number;
  yHeight: number | undefined;
  xWidth: number | undefined;
  color: string | undefined;
  text?: string | undefined;
  [key: string]: any;
} => {
  switch (drawingType) {
    case DrawingType.Ruler:
      const rulerDetails = drawingUpdateDetails as RulerUpdateDetails;
      const { x1, y1, x2, y2, color } = rulerDetails;

      let rulerData = {
        xOffSet: undefined,
        yOffSet: undefined,
        yHeight: undefined,
        xWidth: undefined,
        color: color,
      };

      if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
        const [transformedX1, transformedY1] = tpObject.FromCanvasToDb(x1, y1);
        const [transformedX2, transformedY2] = tpObject.FromCanvasToDb(x2, y2);
        rulerData = {
          ...rulerData,
          xOffSet: transformedX1,
          yOffSet: transformedY1,
          xWidth: transformedX2,
          yHeight: transformedY2,
        };
      }
      return rulerData;
    case DrawingType.POI:
      const poiDetails = drawingUpdateDetails as PoiUpdateDetails;
      const [transformedX, transformedY] = tpObject.FromCanvasToDb(
        poiDetails.x,
        poiDetails.y,
      );
      return {
        xOffSet: transformedX,
        yOffSet: transformedY,
        yHeight: undefined,
        xWidth: undefined,
        color: poiDetails.color,
      };
    case DrawingType.ROI:
      const roiDetails = drawingUpdateDetails as RoiUpdateDetails;
      if (roiDetails.x !== undefined && roiDetails.y !== undefined) {
        const transformedRoiPosition = tpObject.convertRectangleDrawingForDb(
          roiDetails.x,
          roiDetails.y,
          roiDetails.width,
          roiDetails.height,
        );
        return {
          xOffSet: transformedRoiPosition.x,
          yOffSet: transformedRoiPosition.y,
          yHeight: undefined,
          xWidth: undefined,
          color: roiDetails.color,
        };
      } else {
        const transformedRoiSize = tpObject.ConvertPointsToROI({
          xWidth: roiDetails.width,
          yHeight: roiDetails.height,
        });
        return {
          xOffSet: undefined,
          yOffSet: undefined,
          yHeight: transformedRoiSize.height,
          xWidth: transformedRoiSize.width,
          color: roiDetails.color,
        };
      }

    case DrawingType.CRYSTAL:
      const crystalDetails = drawingUpdateDetails as CrystalUpdateDetails;
      if (crystalDetails.x !== undefined && crystalDetails.y !== undefined) {
        const transformedCrystalPosition = tpObject.convertRectangleDrawingForDb(
          crystalDetails.x,
          crystalDetails.y,
          crystalDetails.width,
          crystalDetails.height,
        );
        return {
          xOffSet: transformedCrystalPosition.x,
          yOffSet: transformedCrystalPosition.y,
          yHeight: undefined,
          xWidth: undefined,
          color: undefined,
        };
      } else {
        const transformedCrystalSize = tpObject.ConvertPointsToCrystal(crystalDetails);
        return {
          xOffSet: undefined,
          yOffSet: undefined,
          yHeight: transformedCrystalSize.height,
          xWidth: transformedCrystalSize.width,
          color: undefined,
        };
      }
    case DrawingType.Annotation:
      const annotationDetails = drawingUpdateDetails as AnnotationUpdateDetails;
      const [transformedAnnotationX, transformedAnnotationY] =
        tpObject && annotationDetails.x && annotationDetails.y
          ? tpObject.FromCanvasToDb(annotationDetails.x, annotationDetails.y)
          : [undefined, undefined];
      return {
        xOffSet: transformedAnnotationX,
        yOffSet: transformedAnnotationY,
        yHeight: annotationDetails.height,
        xWidth: annotationDetails.width,
        color: undefined,
        text: annotationDetails.text,
        expanded: annotationDetails.expanded,
      };
    case DrawingType.Scribble:
      const scribbleDetails = drawingUpdateDetails as ScribbleUpdateDetails;
      const { x, y } = scribbleDetails;

      let result = {
        xOffSet: undefined,
        yOffSet: undefined,
        yHeight: undefined,
        xWidth: undefined,
        color: scribbleDetails.color,
      };

      if (x !== undefined && y !== undefined) {
        const [transformedScribbleX, transformedScribbleY] = tpObject.FromCanvasToDb(
          x,
          y,
        );
        result = {
          ...result,
          xOffSet: transformedScribbleX,
          yOffSet: transformedScribbleY,
        };
      }
      return result;

    case DrawingType.DropLocation:
      const dlDetails = drawingUpdateDetails as DropLocationUpdateDetails;
      if (dlDetails.x !== undefined && dlDetails.y !== undefined) {
        const transformedDlPosition = tpObject.convertRectangleDrawingForDb(
          dlDetails.x,
          dlDetails.y,
          dlDetails.width,
          dlDetails.height,
        );
        return {
          yHeight: transformedDlPosition.height,
          xWidth: transformedDlPosition.width,
          xOffSet: transformedDlPosition.x,
          yOffSet: transformedDlPosition.y,
          color: undefined,
        };
      } else {
        const transformedDlSize = tpObject.convertRectangleDrawingForDb(
          dlDetails.x,
          dlDetails.y,
          dlDetails.width,
          dlDetails.height,
        );
        return {
          xOffSet: undefined,
          yOffSet: undefined,
          yHeight: transformedDlSize.height,
          xWidth: transformedDlSize.width,
          color: undefined,
        };
      }
    default:
      return {
        xOffSet: 0,
        yOffSet: 0,
        yHeight: undefined,
        xWidth: undefined,
        color: undefined,
      };
  }
};

export const getTransformedNewDrawingsRequestParameters = (
  drawingType: DrawingType,
  drawingUpdateDetails: DrawingUpdateDetails,
  tpObject: TransformPoints,
) => {
  switch (drawingType) {
    case DrawingType.POI:
      const poiDetails = drawingUpdateDetails as PoiUpdateDetails;
      const poiTransformedPoints = tpObject.FromCanvasToDb(poiDetails.x, poiDetails.y);
      return {
        points: poiTransformedPoints,
        text: poiDetails.name,
        color: poiDetails.color,
      };
    case DrawingType.ROI:
      const roiDetails = drawingUpdateDetails as RoiUpdateDetails;
      const roiTransformedPoints = tpObject.convertDrawingDimensions(roiDetails);
      return {
        text: roiDetails.regionName,
        points: roiTransformedPoints,
      };
    case DrawingType.DropLocation:
      const dlDetails = drawingUpdateDetails as DropLocationUpdateDetails;
      const dlTransformedPoints = tpObject.convertDrawingDimensions(dlDetails);
      return {
        regionName: 'Drop',
        points: dlTransformedPoints,
      };
    case DrawingType.Ruler:
      const rulerDetails = drawingUpdateDetails as RulerUpdateDetails;
      return {
        points: [
          ...tpObject.FromCanvasToDb(rulerDetails.x1, rulerDetails.y1),
          ...tpObject.FromCanvasToDb(rulerDetails.x2, rulerDetails.y2),
        ],
        color: rulerDetails.color,
      };
    case DrawingType.Scribble:
      const scribbleDetails = drawingUpdateDetails as ScribbleUpdateDetails;
      const scribbleTransformedPoints = convertPointsFromCanvasToDB(
        scribbleDetails.x,
        scribbleDetails.y,
        scribbleDetails.points,
        tpObject,
      );
      return {
        points: scribbleTransformedPoints,
        color: scribbleDetails.color,
      };
    case DrawingType.Annotation:
      const annotationDetails = drawingUpdateDetails as AnnotationUpdateDetails;
      const [x, y] = tpObject.FromCanvasToDb(annotationDetails.x, annotationDetails.y);
      return {
        text: annotationDetails.text,
        points: [x, y, annotationDetails.width, annotationDetails.height],
        fontFamily: annotationDetails.fontFamily,
        fontSize: annotationDetails.size,
      };
    case DrawingType.CRYSTAL:
      const crystalDetails = drawingUpdateDetails as CrystalUpdateDetails;
      const crystalTransformedPoints = tpObject.convertDrawingDimensions(crystalDetails);
      return {
        points: crystalTransformedPoints,
        name: crystalDetails.name,
      };
  }
};

/**
 *
 * @param params IWellImageParams
 * @returns well image(fetching) details as a key, which will be used in redux to uniquely identify well image.
 * the key is based on all values sitched by '-'. This key making pattern should be consistent and only this function should be used.
 *
 */
export const getWellDropImageUniqueKey = (params: IWellImageParams): string => {
  if (!params) {
    return '';
  }

  return `${params.pid}-${params.wellNumber}-${params.batchId}-${params.dropNumber}-${params.cpID}-${params.regionID}`;
};

export const getWellImagingDetailsKey = (
  imageBatchId: number,
  cpId: number,
  regionId: number,
): string => {
  return `${imageBatchId}-${cpId}-${regionId}`;
};

export const getWellDropImageUniqueKeyByWellDetails = (
  wellDetails: WellDetailsStates,
): string => {
  if (!wellDetails) {
    return '';
  }

  return getWellDropImageUniqueKey(getWellImageParams(wellDetails));
};

export const getWellDropImageUniqueKeyByWellNavigationDetails = (
  wellNavigationDetails: WellNavigationDetails,
  dropImageParameters: DropImageParameters,
  allRegions?: any,
): string => {
  if (!wellNavigationDetails || !dropImageParameters) {
    return '';
  }

  return getWellDropImageUniqueKey({
    pid: wellNavigationDetails?.plateID,
    wellNumber: wellNavigationDetails?.wellNum,
    batchId: dropImageParameters?.inspection?.groupedInspections?.find(
      (gi) => gi.plateId === wellNavigationDetails?.plateID,
    )?.imageBatchId,
    dropNumber: dropImageParameters?.dropNumber,
    cpID: dropImageParameters?.cpId,
    regionID: getRegionIdCached(
      wellNavigationDetails?.plateID,
      wellNavigationDetails?.wellNum,
      dropImageParameters?.dropNumber,
      dropImageParameters?.regionName,
      allRegions,
    ),
  } as IWellImageParams);
};
